import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { Heart, Search, ShoppingCart, User as UserIcon, Menu, ChevronRight, TrendingUp, Tag, Sparkles, Sun, Moon, Package, Gamepad2, TreePine, Camera, Loader2, Info, Mail, Phone } from "lucide-react";
import { Suspense, lazy, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useWishlist } from "@/hooks/storefront/use-wishlist";
import { useCart } from "@/hooks/storefront/use-cart";
import { useAuth } from "@/hooks/use-auth";
import { useTheme } from "@/hooks/use-theme";
import { listCategories } from "@/lib/products.functions";
import { useSearchIndex } from "@/hooks/storefront/use-search-index";
import type { SearchProductRow } from "@/lib/products.functions";
import { tokenizeSearch } from "@/lib/search-utils";
import { useServerFn } from "@tanstack/react-start";
import { imageToSearchQuery, logSearch } from "@/lib/ai-search.functions";
import { toast } from "sonner";

import { getRecentSearches, pushRecentSearch, clearRecentSearches } from "@/lib/recent-searches";
import { productSlug } from "@/lib/format";

import { buildCategoryIconMap, categorySlug } from "@/lib/category-icon";
import { CategoryIcon } from "@/components/storefront/CategoryIcon";
const SearchSuggestions = lazy(() =>
  import("@/components/storefront/SearchSuggestions").then((m) => ({ default: m.SearchSuggestions })),
);
const BackgroundMusic = lazy(() =>
  import("@/components/storefront/BackgroundMusic").then((m) => ({ default: m.BackgroundMusic })),
);
import { openPandaGame } from "@/components/storefront/panda-game-bus";
import { PandakiButton } from "@/components/storefront/PandakiButton";
import logo from "@/assets/logo.webp";
import logoSm from "@/assets/logo-sm.webp";
import logoWhite from "@/assets/logo-white.webp";
import logoWhiteSm from "@/assets/logo-white-sm.webp";
// Τα Radix overlays φορτώνονται μόνο όταν ανοίξουν (λιγότερο JS στο πρώτο load).
const HeaderCategorySheet = lazy(() =>
  import("@/components/storefront/HeaderOverlays").then((m) => ({ default: m.HeaderCategorySheet })),
);
const HeaderSearchDialog = lazy(() =>
  import("@/components/storefront/HeaderOverlays").then((m) => ({ default: m.HeaderSearchDialog })),
);

/**
 * Ενιαίο στυλ για όλα τα κουμπιά της πάνω κορδέλας: ίδιο φόντο, ίδιο
 * περίγραμμα, ίδιο μέγεθος και ένα μόνο εικονίδιο αριστερά. Τα κουμπιά δεν
 * μεγαλώνουν στο hover — το πεδίο τους έχει overflow-hidden, οπότε θα
 * κόβονταν από πάνω.
 */
const RIBBON_ITEM =
  "shrink-0 items-center gap-1.5 rounded-full bg-primary-foreground/12 px-2 py-1 text-xs font-bold text-primary-foreground ring-1 ring-primary-foreground/25 transition-colors hover:bg-primary-foreground/22";

export function Header({ showCart = false }: { showCart?: boolean }) {
  return <HeaderInner showCart={showCart} />;
}

function ThemeToggle() {
  const { isDark, toggleTheme } = useTheme();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      onClick={toggleTheme}
      aria-label={isDark ? "Εναλλαγή σε φωτεινό θέμα" : "Εναλλαγή σε σκούρο θέμα"}
      title={isDark ? "Φωτεινό θέμα" : "Σκούρο θέμα"}
      className="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full bg-primary-foreground/20 ring-1 ring-primary-foreground/25 transition-colors hover:bg-primary-foreground/30"
    >
      <span
        className={`grid h-4 w-4 place-items-center rounded-full bg-primary-foreground text-primary shadow-sm transition-transform duration-200 ${
          isDark ? "translate-x-4" : "translate-x-0.5"
        }`}
      >
        {isDark ? <Moon className="h-2.5 w-2.5" /> : <Sun className="h-2.5 w-2.5" />}
      </span>
    </button>
  );
}

const EMPTY_OUTCOME = {
  items: [] as SearchProductRow[],
  mode: "empty" as const,
  didYouMean: null,
  brands: [] as { id: number; name: string; count: number }[],
  categories: [] as { id: number; name: string; count: number }[],
};

const TRENDING_TERMS = [
  "χαρτί υγείας",
  "απορρυπαντικό ρούχων",
  "νερό",
  "καφές",
  "σαμπουάν",
  "χαρτοπετσέτες",
  "οδοντόκρεμα",
  "απολυμαντικό",
];

function HeaderInner({ showCart = false }: { showCart?: boolean }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [desktopFocused, setDesktopFocused] = useState(false);
  const [warm, setWarm] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recent, setRecent] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const desktopSearchRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  // Το ύψος του header γίνεται CSS μεταβλητή, ώστε τα slide-out panels
  // (μενού κατηγοριών) να ξεκινούν από κάτω του και να μην το σκεπάζουν.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const publish = () =>
      document.documentElement.style.setProperty("--mp-header-h", `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener("resize", publish);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", publish);
    };
  }, []);
  const wishlist = useWishlist();
  const cart = useCart();
  const { user } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const userMeta = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const displayName =
    (typeof userMeta.full_name === "string" && userMeta.full_name.trim()) ||
    [userMeta.first_name ?? userMeta.firstname, userMeta.last_name ?? userMeta.lastname]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    null;

  useEffect(() => {
    setQ("");
    setSearchOpen(false);
    setDesktopFocused(false);
  }, [pathname]);

  // Πολύ μικρό debounce: η αναζήτηση τρέχει τοπικά πάνω σε ευρετήριο (ms).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 35);
    return () => clearTimeout(t);
  }, [q]);

  // Προθέρμανση του καταλόγου αναζήτησης όταν το πρόγραμμα περιήγησης είναι
  // ανενεργό, ώστε τα αποτελέσματα να εμφανίζονται ακαριαία στην πρώτη πληκτρολόγηση.
  useEffect(() => {
    // Η προθέρμανση (κατέβασμα καταλόγου + χτίσιμο ευρετηρίου) είναι βαριά
    // εργασία για το κύριο νήμα, οπότε ξεκινά μόνο μετά την πρώτη
    // αλληλεπίδραση του χρήστη — όχι κατά την αρχική φόρτωση.
    const events = ["pointerdown", "pointermove", "keydown", "touchstart"] as const;
    const kick = () => {
      for (const ev of events) window.removeEventListener(ev, kick);
      window.clearTimeout(fallback);
      setWarm(true);
    };
    const fallback = window.setTimeout(kick, 12000);
    for (const ev of events) window.addEventListener(ev, kick, { once: true, passive: true });
    return () => {
      for (const ev of events) window.removeEventListener(ev, kick);
      window.clearTimeout(fallback);
    };
  }, []);

  useEffect(() => {
    if (searchOpen) {
      // Το dialog φορτώνεται lazy: δοκιμάζουμε επανειλημμένα μέχρι να υπάρχει.
      let tries = 0;
      const id = setInterval(() => {
        tries += 1;
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          clearInterval(id);
        } else if (tries > 40) {
          clearInterval(id);
        }
      }, 50);
      return () => clearInterval(id);
    }
  }, [searchOpen]);

  // Close the desktop suggestions dropdown when clicking outside of it.
  useEffect(() => {
    if (!desktopFocused) return;
    const onDown = (e: MouseEvent) => {
      if (desktopSearchRef.current && !desktopSearchRef.current.contains(e.target as Node)) {
        setDesktopFocused(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [desktopFocused]);

  const panelOpen = desktopFocused || searchOpen;

  // ---- Αναζήτηση με φωτογραφία --------------------------------------------
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [imageBusy, setImageBusy] = useState(false);
  const imageToQuery = useServerFn(imageToSearchQuery);
  const logSearchFn = useServerFn(logSearch);
  const onPickImage = async (file: File | undefined) => {
    if (!file) return;
    setImageBusy(true);
    try {
      const dataUrl = await resizeImageToDataUrl(file, 768);
      const r = await imageToQuery({ data: { dataUrl } });
      if (r.query) {
        setQ(r.query);
        setDebouncedQ(r.query);
        void logSearchFn({ data: { query: r.query, results: 1, source: "image" } }).catch(() => {});
        if (!searchOpen && !desktopFocused) {
          if (window.matchMedia("(min-width: 1024px)").matches) setDesktopFocused(true);
          else setSearchOpen(true);
        }
      } else {
        toast.error(
          r.error === "rate"
            ? "Πολλές αναζητήσεις με φωτογραφία. Δοκίμασε ξανά σε λίγα λεπτά."
            : r.error === "notfound"
              ? "Δεν αναγνώρισα προϊόν στη φωτογραφία. Δοκίμασε πιο κοντινή λήψη."
              : "Η αναζήτηση με φωτογραφία δεν είναι διαθέσιμη αυτή τη στιγμή.",
        );
      }
    } catch {
      toast.error("Δεν μπόρεσα να διαβάσω τη φωτογραφία.");
    } finally {
      setImageBusy(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };
  const cameraButton = (cls: string) => (
    <button
      type="button"
      aria-label="Αναζήτηση με φωτογραφία"
      title="Αναζήτηση με φωτογραφία"
      disabled={imageBusy}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => imageInputRef.current?.click()}
      className={`grid h-8 w-8 place-items-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-primary disabled:opacity-60 ${cls}`}
    >
      {imageBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
    </button>
  );
  const enabled = debouncedQ.length >= 2;

  // Ο κατάλογος «ζεσταίνεται» μόλις ο χρήστης πλησιάσει το πεδίο αναζήτησης,
  // ώστε τα αποτελέσματα να είναι ήδη έτοιμα όταν αρχίσει να γράφει.
  const { run, isReady } = useSearchIndex(warm || panelOpen || enabled);
  const isFetching = enabled && !isReady;
  const tokens = useMemo(() => tokenizeSearch(debouncedQ), [debouncedQ]);
  const outcome = useMemo(
    () => (enabled && isReady ? run(debouncedQ, 200) : EMPTY_OUTCOME),
    [enabled, isReady, run, debouncedQ],
  );
  const suggestions = outcome.items.slice(0, 8);

  useEffect(() => {
    setActiveIndex(-1);
  }, [debouncedQ]);

  useEffect(() => {
    if (panelOpen) setRecent(getRecentSearches());
  }, [panelOpen]);

  const goSearch = (term: string) => {
    const t = term.trim();
    if (!t) return;
    pushRecentSearch(t);
    setRecent(getRecentSearches());
    setDesktopFocused(false);
    setSearchOpen(false);
    navigate({ to: "/search", search: { q: t } });
  };

  const openProduct = (p: SearchProductRow) => {
    pushRecentSearch(debouncedQ);
    setDesktopFocused(false);
    setSearchOpen(false);
    navigate({ to: "/p/$slug", params: { slug: productSlug(p.name ?? "", p.ps_id, p.id) } });
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (suggestions.length ? (i + 1) % suggestions.length : -1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (suggestions.length ? (i <= 0 ? suggestions.length - 1 : i - 1) : -1));
    } else if (e.key === "Escape") {
      setDesktopFocused(false);
      setSearchOpen(false);
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && suggestions[activeIndex]) {
        e.preventDefault();
        openProduct(suggestions[activeIndex]);
      }
    }
  };

  const panelProps = {
    query: debouncedQ,
    tokens,
    outcome,
    isFetching,
    activeIndex,
    recent,
    trending: TRENDING_TERMS,
    onHoverItem: setActiveIndex,
    onPickTerm: (t: string) => {
      setQ(t);
      setDebouncedQ(t);
      searchInputRef.current?.focus();
    },
    onClearRecent: () => {
      clearRecentSearches();
      setRecent([]);
    },
  };



  const [catOpen, setCatOpen] = useState(false);
  const { data: catData } = useQuery({
    queryKey: ["categories"],
    queryFn: () => listCategories(),
    staleTime: 5 * 60_000,
  });
  const categories = catData?.categories ?? [];
  useMemo(() => {
    if (categories.length > 0) buildCategoryIconMap(categories);
  }, [categories]);

  type Cat = {
    id: string; ps_id: number; name: string;
    parent_ps_id: number | null; level_depth: number | null; position: number | null;
  };
  const { roots, childrenByParent } = useMemo(() => {
    const list = (categories as Cat[]) ?? [];
    const ids = new Set(list.map((c) => c.ps_id));
    const minDepth = list.reduce(
      (m, c) => (c.level_depth != null && c.level_depth < m ? c.level_depth : m),
      Number.POSITIVE_INFINITY,
    );
    const childrenByParent = new Map<number, Cat[]>();
    const roots: Cat[] = [];
    for (const c of list) {
      const isRoot = c.parent_ps_id == null || !ids.has(c.parent_ps_id) || c.level_depth === minDepth;
      if (isRoot) {
        roots.push(c);
      } else {
        const arr = childrenByParent.get(c.parent_ps_id!) ?? [];
        arr.push(c);
        childrenByParent.set(c.parent_ps_id!, arr);
      }
    }
    const sortFn = (a: Cat, b: Cat) =>
      (a.position ?? 0) - (b.position ?? 0) || a.name.localeCompare(b.name);
    roots.sort(sortFn);
    for (const arr of childrenByParent.values()) arr.sort(sortFn);
    return { roots, childrenByParent };
  }, [categories]);

  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const toggle = (psId: number) =>
    setExpanded((prev) => ({ ...prev, [psId]: !prev[psId] }));

  const renderNode = (c: Cat, depth: number) => (
    <CategoryNode
      key={c.id}
      cat={c}
      depth={depth}
      kids={childrenByParent.get(c.ps_id) ?? []}
      isOpen={!!expanded[c.ps_id]}
      onToggle={() => toggle(c.ps_id)}
      onNavigate={() => setCatOpen(false)}
      renderChild={renderNode}
    />
  );

  const isWide = useMinWidth(640);

  return (
    <header ref={headerRef} className="xmas-header sticky top-0 z-40 border-b border-border bg-background">
      {/* 🎄 Γιορτινή διακόσμηση — μόνο οπτικά, μόνο σε μεγάλες οθόνες */}
      {isWide && (
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          {/* Μικρά γιορτινά αυτοκόλλητα */}
          <span className="absolute left-[18%] top-1 hidden text-lg opacity-35 lg:block">🎄</span>
          <span className="absolute left-[38%] top-0.5 hidden text-lg opacity-30 lg:block">✨</span>
          <span className="absolute right-[22%] top-1 hidden text-lg opacity-30 lg:block">🎁</span>
          <span className="absolute left-[28%] bottom-1 hidden text-base opacity-30 lg:block">❄️</span>
          <span className="absolute right-[34%] bottom-0.5 hidden text-base opacity-30 lg:block">🕯️</span>
          <span className="absolute right-[10%] bottom-1 hidden text-base opacity-25 lg:block">⭐</span>
          <span className="absolute left-[8%] bottom-0.5 hidden text-base opacity-25 md:block">🔔</span>
        </div>
      )}
      {/* Το υπάρχον περιεχόμενο μπαίνει πάνω από την διακόσμηση */}
      <div className="relative z-10">
      {/* Στα lg+ το λουρί γίνεται fixed ώστε να απλώνει πέρα για πέρα, πάνω και
          από το πλαϊνό καλάθι· κρατάμε κενό ίδιου ύψους για να μη μετατοπίζεται
          το υπόλοιπο header. */}
      <div aria-hidden className="hidden h-9 border-b border-primary-foreground/20 lg:block" />
      {isWide && (
        <div className="hidden border-b border-primary-foreground/20 bg-primary sm:block lg:fixed lg:inset-x-0 lg:top-[var(--announce-h,0px)] lg:z-50">
          <div className="mx-auto grid h-9 max-w-none grid-cols-[minmax(0,1fr)_auto_minmax(0,auto)] items-center gap-2 px-4 text-xs text-primary-foreground">
            <nav className="hidden min-w-0 items-center gap-2 overflow-hidden whitespace-nowrap sm:flex">
              <a href="https://www.facebook.com/mrpandagr/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className={`${RIBBON_ITEM} inline-flex h-6`}>
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a href="https://www.instagram.com/mrpandagr/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className={`${RIBBON_ITEM} inline-flex h-6`}>
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                </svg>
              </a>
              <Link to="/christmas" aria-label="Χριστούγεννα" className="xmas-pill hidden shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-xs font-bold ring-1 ring-primary-foreground/25 md:inline-flex"><TreePine className="h-3.5 w-3.5" /><span className="hidden lg:inline">Χριστούγεννα</span></Link>
              <PandakiButton variant="strip" />
              <Link to="/best-sellers" aria-label="Κορυφαία σε Πωλήσεις" className={`${RIBBON_ITEM} hidden md:inline-flex`}><TrendingUp className="h-3.5 w-3.5" /><span className="hidden lg:inline">Κορυφαία σε Πωλήσεις</span></Link>
              <Link to="/offers" aria-label="Προσφορές" className={`${RIBBON_ITEM} hidden md:inline-flex`}><Tag className="h-3.5 w-3.5" /><span className="hidden lg:inline">Προσφορές</span></Link>
              <Link to="/new-products" aria-label="Νέες αφίξεις" className={`${RIBBON_ITEM} hidden md:inline-flex`}><Sparkles className="h-3.5 w-3.5" /><span className="hidden lg:inline">Νέες αφίξεις</span></Link>
              <Link to="/mrbox" aria-label="MrBox" className={`${RIBBON_ITEM} hidden md:inline-flex`}><Package className="h-3.5 w-3.5" /><span className="hidden xl:inline">MrBox</span></Link>
              <button
                type="button"
                onClick={() => openPandaGame()}
                title="Παίξε & κέρδισε κουπόνι"
                aria-label="Παίξε & κέρδισε κουπόνι"
                className={`${RIBBON_ITEM} hidden md:inline-flex`}
              >
                <Gamepad2 className="h-3.5 w-3.5" /><span className="hidden xl:inline">Παίξε</span>
              </button>
            </nav>
            <div className="flex shrink-0 justify-center">
              <Suspense fallback={null}><BackgroundMusic /></Suspense>
            </div>
            <nav className="hidden min-w-0 items-center justify-end gap-2 overflow-hidden whitespace-nowrap sm:flex">
              <ThemeToggle />
              <Link to="/about" aria-label="Σχετικά με εμάς" className={`${RIBBON_ITEM} hidden md:inline-flex`}><Info className="h-3.5 w-3.5" /><span className="hidden lg:inline">Σχετικά με εμάς</span></Link>
              <Link to="/contact" aria-label="Επικοινωνία" className={`${RIBBON_ITEM} hidden md:inline-flex`}><Mail className="h-3.5 w-3.5" /><span className="hidden lg:inline">Επικοινωνία</span></Link>
              <a href="tel:2311822500" aria-label="Τηλέφωνο 2311 822 500" className={`${RIBBON_ITEM} hidden md:inline-flex`}><Phone className="h-3.5 w-3.5" /><span className="hidden lg:inline">2311 822 500</span></a>
            </nav>
          </div>
        </div>
      )}

      <div className="mx-auto flex lg:grid lg:grid-cols-[auto_1fr_auto] max-w-none items-center gap-x-3 gap-y-2 px-4 py-3">
        <div className="flex items-center gap-2">
          <a
            href="/"
            onClick={(e) => {
              if (pathname === "/") {
                e.preventDefault();
                window.location.reload();
              }
            }}
            className="order-2 lg:order-1 flex items-center gap-1.5 shrink"
            aria-label="MrPanda αρχική σελίδα"
          >
            <img src={logo} srcSet={`${logoSm} 244w, ${logo} 360w`} sizes="(max-width: 639px) 122px, 182px" alt="MrPanda.gr λογότυπο" width={360} height={95} className="h-8 w-[122px] object-contain object-left sm:h-12 sm:w-[182px] dark:hidden" />
            <img src={logoWhite} srcSet={`${logoWhiteSm} 244w, ${logoWhite} 360w`} sizes="(max-width: 639px) 122px, 182px" alt="MrPanda.gr λογότυπο" width={360} height={95} className="hidden h-8 w-[122px] object-contain object-left sm:h-12 sm:w-[182px] dark:block" />
          </a>
          <div className="order-1 lg:order-2">
            <button
              type="button"
              onClick={() => setCatOpen(true)}
              aria-label="Κατηγορίες"
              className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1.5 font-medium text-foreground hover:bg-muted lg:px-3"
            >
              <Menu className="h-5 w-5 lg:h-4 lg:w-4" /> <span className="hidden lg:inline">Κατηγορίες</span>
            </button>
            {catOpen && (
              <Suspense fallback={null}>
                <HeaderCategorySheet open={catOpen} onOpenChange={setCatOpen}>
                  <Link
                    to="/mrbox"
                    onClick={() => setCatOpen(false)}
                    className="mb-2 flex items-center gap-2 rounded-2xl bg-primary/10 px-3 py-2.5 text-sm font-bold text-primary"
                  >
                    <Package className="h-4 w-4" /> MrBox — Έτοιμα πακέτα 📦
                  </Link>
                  <PandakiButton
                    variant="sheet"
                    className="mb-2"
                    onNavigate={() => setCatOpen(false)}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setCatOpen(false);
                      openPandaGame();
                    }}
                    className="mb-2 flex items-center gap-2 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-3 py-2.5 text-sm font-bold text-white"
                  >
                    <Gamepad2 className="h-4 w-4" /> Παίξε & Κέρδισε 🎮
                  </button>
                  <Link
                    to="/christmas"
                    onClick={() => setCatOpen(false)}
                    className="xmas-sheet-link mb-2 flex items-center gap-2 rounded-2xl px-3 py-2.5 text-sm font-bold"
                  >
                    <TreePine className="h-4 w-4" /> Χριστούγεννα 🎄
                  </Link>
                  {categories.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">Φόρτωση... 🐾</div>
                  ) : (
                    roots.map((c) => renderNode(c, 0))
                  )}
                </HeaderCategorySheet>
              </Suspense>
            )}
          </div>
        </div>

        <div
          ref={desktopSearchRef}
          onPointerEnter={() => setWarm(true)}
          className="relative hidden lg:flex w-full justify-self-center"
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              goSearch(q);
            }}
            className="w-full"
          >
            <div className="relative w-full">
              <Search className={`absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors ${desktopFocused ? "text-primary" : "text-muted-foreground"}`} />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => setDesktopFocused(true)}
                onKeyDown={onSearchKeyDown}
                aria-label="Αναζήτηση προϊόντων"
                placeholder="Τί ψάχνεις;"
                className={`h-11 w-full rounded-full border bg-muted/40 pl-10 pr-32 text-sm text-foreground placeholder:text-muted-foreground outline-none transition-all ${
                  desktopFocused ? "border-primary bg-background shadow-lg ring-4 ring-primary/10" : "border-border hover:border-primary/50"
                }`}
              />
              {q ? (
                <button
                  type="button"
                  aria-label="Καθαρισμός"
                  onClick={() => { setQ(""); setDebouncedQ(""); searchInputRef.current?.focus(); }}
                  className="absolute right-28 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  ×
                </button>
              ) : null}
              {cameraButton("absolute right-[4.75rem] top-1/2 -translate-y-1/2")}
              <button
                type="submit"
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground transition-transform hover:scale-105 active:scale-95"
              >
                Ψάξε
              </button>
            </div>
          </form>
          {desktopFocused && (
            <div className="absolute left-0 right-0 top-[52px] z-50 max-h-[70vh] animate-pop-in overflow-auto rounded-3xl border border-border bg-background/95 shadow-2xl backdrop-blur">
              <Suspense fallback={null}>
                <SearchSuggestions
                  {...panelProps}
                  onSelect={() => { pushRecentSearch(debouncedQ); setRecent(getRecentSearches()); setDesktopFocused(false); }}
                  onSeeAll={() => goSearch(debouncedQ)}
                />
              </Suspense>
            </div>
          )}
        </div>


        <div className="ml-auto flex items-center gap-1 text-sm">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Αναζήτηση"
            className="mp-bouncy flex h-11 items-center gap-2 rounded-full px-2 lg:px-3 text-foreground hover:bg-muted lg:hidden"
          >
            <Search className="h-5 w-5" />
            <span className="hidden sm:inline">Αναζήτηση</span>
          </button>
          <Link
            to={user ? "/account" : "/login"}
            aria-label={user ? "Λογαριασμός" : "Σύνδεση"}
            className="mp-bouncy flex h-11 items-center gap-2 rounded-full px-2 lg:px-3 text-foreground hover:bg-muted"
          >
            <UserIcon className="h-5 w-5" />
            <span className="hidden sm:inline">
              {displayName ?? (user ? "Λογαριασμός" : "Σύνδεση")}
            </span>
          </Link>
          <Link
            to="/wishlist"
            aria-label="Αγαπημένα"
            className="mp-bouncy relative flex h-11 items-center gap-2 rounded-full px-2 lg:px-3 text-foreground hover:bg-muted"
          >
            <Heart className="h-5 w-5" />
            <span className="hidden sm:inline">Αγαπημένα</span>
            {wishlist.count > 0 && (
              <span className="absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                {wishlist.count}
              </span>
            )}
          </Link>
          {showCart && (
            <Link
              to="/cart"
              aria-label="Καλάθι"
              className="mp-bouncy relative flex h-11 items-center gap-2 rounded-full px-2 lg:px-3 text-foreground hover:bg-muted"
            >
              <ShoppingCart className="h-5 w-5" />
              <span className="hidden sm:inline">Καλάθι</span>
              {cart.count > 0 && (
                <span className="absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                  {cart.count}
                </span>
              )}
            </Link>
          )}
        </div>
      </div>

      {searchOpen && (
        <Suspense fallback={null}>
          <HeaderSearchDialog open={searchOpen} onOpenChange={setSearchOpen}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              goSearch(q);
            }}
            className="relative border-b border-border bg-gradient-to-r from-primary/10 to-transparent"
          >
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-primary" />
            <input
              ref={searchInputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onSearchKeyDown}
              enterKeyHint="search"
              placeholder="Τί ψάχνεις;"
              className="h-14 w-full bg-transparent pl-12 pr-32 text-base outline-none"
            />
            {q && (
              <button
                type="button"
                aria-label="Καθαρισμός"
                onClick={() => { setQ(""); setDebouncedQ(""); searchInputRef.current?.focus(); }}
                className="absolute right-24 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-muted-foreground hover:bg-muted"
              >
                ×
              </button>
            )}
            {cameraButton("absolute right-[3.75rem] top-1/2 -translate-y-1/2")}
            <button
              type="submit"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground active:scale-95"
            >
              Ψάξε
            </button>
          </form>
          <div className="max-h-[65vh] overflow-auto overscroll-contain">
            <SearchSuggestions
              {...panelProps}
              onSelect={() => { pushRecentSearch(debouncedQ); setRecent(getRecentSearches()); setSearchOpen(false); }}
              onSeeAll={() => goSearch(debouncedQ)}
            />
          </div>

          </HeaderSearchDialog>
        </Suspense>
      )}

      </div>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => void onPickImage(e.target.files?.[0])}
      />
    </header>
  );
}

function CategoryNode({
  cat, depth, kids, isOpen, onToggle, onNavigate, renderChild,
}: {
  cat: { id: string; ps_id: number; name: string };
  depth: number;
  kids: { id: string; ps_id: number; name: string; parent_ps_id: number | null; level_depth: number | null; position: number | null }[];
  isOpen: boolean;
  onToggle: () => void;
  onNavigate: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderChild: (c: any, depth: number) => ReactNode;
}) {
  const hasKids = kids.length > 0;
  return (
    <div>
      <div className="flex items-center gap-1" style={{ paddingLeft: depth * 12 }}>
        {hasKids ? (
          <button
            type="button"
            onClick={onToggle}
            aria-label={isOpen ? "Σύμπτυξη" : "Επέκταση"}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-primary/15 hover:text-primary active:scale-90"
          >
            <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
          </button>
        ) : (
          <span className="h-7 w-7 shrink-0" />
        )}
        <Link
          to="/c/$slug"
          params={{ slug: categorySlug(cat.ps_id, cat.name) }}
          onClick={onNavigate}
          className="flex flex-1 items-center gap-2 rounded-xl px-2 py-2 text-sm text-foreground transition-all hover:translate-x-0.5 hover:bg-primary/10 hover:text-primary"
        >
          <CategoryIcon psId={cat.ps_id} className="h-5 w-5 shrink-0" />
          <span>{cat.name}</span>
        </Link>
      </div>
      {hasKids && isOpen && (
        <div>{kids.map((k) => renderChild(k, depth + 1))}</div>
      )}
    </div>
  );
}

/**
 * true μόνο μετά την ενυδάτωση σε οθόνες >= px.
 * Έτσι τα desktop-only τμήματα δεν υπάρχουν καθόλου στο DOM των κινητών
 * (μικρότερο DOM => λιγότερο style/layout work).
 */
function useMinWidth(px: number) {
  const [wide, setWide] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${px}px)`);
    const onChange = () => setWide(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [px]);
  return wide;
}

/** Σμικρύνει τη φωτογραφία στον browser ώστε να στέλνεται γρήγορα (JPEG). */
async function resizeImageToDataUrl(file: File, max: number): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.82);
  } finally {
    URL.revokeObjectURL(url);
  }
}
